// Vendor
import { inject, observer } from 'mobx-react';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

// Bernie
import { AnalyticsStore, mergeQueryString, updateSearch } from 'bernie-core';
import { ILocalization } from 'bernie-l10n';

// Stores
import { MultiItemStore } from 'stores/multi-item-store';
import { CurrentHotelStore } from 'stores/current-hotel-store';
import { MultiItemStoreStatus } from 'stores/multi-item-store-util';
import { rewriteHistory } from 'utils/route-store-utils';
import { SearchCriteriaModel } from 'src/common/stores/models/search-criteria-model';
import { SearchResultsStore } from 'stores/search-results-store';

// Models
import { BasicError, InCartProperty } from 'models/multi-item/multi-item-types';

// UITK
import { UitkCard, UitkCardContentSection, UitkCardLink } from 'uitk-react-cards';
import { UitkDialogAnimationType, UitkDialogContent, UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkErrorSummary } from 'uitk-react-error-summary';
import { UitkIcon } from 'uitk-react-icons';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkPageLoader } from 'uitk-react-loader';
import { UitkText } from 'uitk-react-text';
import { UitkToolbar } from 'uitk-react-toolbar';

// Utils
import { generateAddAnotherPropertyURL } from './utils';

// Components
import { HoldingAreaActions } from 'components/multi-item/holding-area-actions';
import { History, Location } from 'history';
import { InCartListing } from 'components/multi-item/holding-area-card';

export interface HoldingAreaContentProps extends Partial<RouteComponentProps> {
  analytics?: AnalyticsStore;
  cartItemDeleter: (id) => () => void;
  closeDialog: () => void;
  getCartItems: () => Promise<InCartProperty[]>;
  goToCheckout: () => void;
  isAllSoldOut: () => boolean;
  l10n: ILocalization;
  currentHotel?: CurrentHotelStore;
  multiItem?: MultiItemStore;
  searchCriteria: SearchCriteriaModel;
  searchResults?: SearchResultsStore;
  error?: BasicError;
  selectTitleText: () => string;
}

export interface HoldingAreaContentState {
  isNotFullCart: boolean;
}

@withRouter
@inject('analytics', 'currentHotel', 'multiItem', 'searchResults')
@observer
export class HoldingAreaContent extends React.Component<HoldingAreaContentProps, HoldingAreaContentState> {
  public constructor(props) {
    super(props);

    this.addAnotherUrl = this.addAnotherUrl.bind(this);
    this.trackAddAnotherClick = this.trackAddAnotherClick.bind(this);

    this.state = {
      isNotFullCart: true,
    };
  }

  private addAnotherUrl(): object {
    const { searchCriteria, searchResults } = this.props;

    return generateAddAnotherPropertyURL(searchCriteria, searchResults);
  }

  private scrollAndRoute(history: History, location: Location, roomIndex: number, businessModelFilter: string) {
    const routeQuery = mergeQueryString(location.search, { roomIndex, businessModelFilter }, key => !key.startsWith('pwaDialog'));

    const queryCombiner = (prevQuery, newQuery) => newQuery;

    const scrollQuery = mergeQueryString(location.search, { scrollPosition: 'searchForm' }, key => !key.startsWith('pwaDialog'));

    updateSearch({ history, location, newParams: scrollQuery, replace: true, queryCombiner });
    rewriteHistory(history, { search: routeQuery });
  }

  private addMixAndMatchUrl(): void {
    const {
      multiItem: { itemCount, cartInfo },
      history,
      location,
    } = this.props;
    const roomIndex = itemCount;

    const businessModelFilter = cartInfo && cartInfo.type;

    this.scrollAndRoute(history, location, roomIndex, businessModelFilter);
  }

  private trackAddAnotherClick(rfrr): void {
    const { analytics } = this.props;
    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent(rfrr, 'Add Another Property', null, null, true);
    }
  }

  public componentDidUpdate(_prevProps, prevState) {
    const { currentHotel, multiItem } = this.props;
    const isNotFullCart =
      multiItem.showMixMatchExperience &&
      currentHotel &&
      currentHotel.searchCriteria &&
      currentHotel.searchCriteria.rooms &&
      multiItem.itemCount < currentHotel.searchCriteria.rooms.length;

    if (prevState.isNotFullCart !== isNotFullCart) {
      this.setState({
        isNotFullCart,
      });
    }
  }

  public render() {
    const {
      analytics,
      cartItemDeleter,
      closeDialog,
      getCartItems,
      goToCheckout,
      isAllSoldOut,
      l10n,
      multiItem,
      currentHotel,
      selectTitleText,
    } = this.props;

    const { items, status, error } = multiItem;
    const formattedDate = date => l10n.formatDateString(date, { skeleton: 'MMMEd' });
    const processing = [MultiItemStoreStatus.FETCHING, MultiItemStoreStatus.PREPARE_CHECKOUT_PROCESSING].includes(status);
    const titleText = l10n.formatText(selectTitleText(), multiItem.itemCount);

    return (
      <UitkFullscreenDialog
        openCallback={getCartItems}
        dialogShow={true}
        animation={UitkDialogAnimationType.FADE}
        returnFocusOnClose={true}
      >
        <UitkToolbar
          action={closeDialog}
          analytics={analytics.setupTracker('MultiProp', 'Collapse Holding Area')}
          header={titleText}
          iconLabel={l10n.formatText('hotels.infosite.paymentOptions.cancelButton.a11yLabel')}
        />
        <UitkDialogContent className="all-b-padding-twelve" zeroPadding={true}>
          <div className="all-b-padding-twelve">
            <UitkPageLoader visible={processing} />
            {error && error.message && (
              <div className="all-x-padding-three all-t-padding-three">
                <UitkErrorSummary heading={error.message} link={undefined} linkClickFocus={{}} linkClickFocusId="" />
              </div>
            )}
            {multiItem.showMixMatchExperience && items && items[0] && (
              <div className="all-x-padding-three all-t-padding-three">
                <p>{`${items[0].city}: ${formattedDate(items[0].checkin)} - ${formattedDate(items[0].checkout)}`}</p>
                <h3 className="uitk-cell uitk-type-400 pwa-theme--grey-900 truncate" data-stid="content-hotel-title">
                  {items[0].name}
                </h3>
              </div>
            )}
            <ul className="uitk-list">
              {items &&
                items.map(
                  (item, index) =>
                    item &&
                    item.id && (
                      <li key={item.id} className="all-x-padding-three all-t-padding-three">
                        {!multiItem.showMixMatchExperience && (
                          <h3 className="all-b-padding-two">
                            {`${item.city}: ${formattedDate(item.checkin)} - ${formattedDate(item.checkout)}`}
                          </h3>
                        )}
                        <InCartListing
                          {...item}
                          cartItemDeleter={cartItemDeleter}
                          isFirstItem={index === 0}
                          removeText={
                            multiItem.showMixMatchExperience
                              ? l10n.formatText('multiProperty.playback.mixAndMatch.changeRoom')
                              : l10n.formatText('remove')
                          }
                          analytics={analytics}
                          itemIndex={index}
                          l10n={l10n}
                          multiItem={multiItem}
                          useRewards={currentHotel && currentHotel.searchCriteria && currentHotel.searchCriteria.useRewards}
                          scrollAndRoute={this.scrollAndRoute}
                        />
                      </li>
                    )
                )}
              <li className="all-x-padding-three all-t-padding-three">
                {!multiItem.showMixMatchExperience && (
                  <UitkCard>
                    <UitkLayoutFlex>
                      <UitkLayoutFlexItem alignSelf="center" grow={0}>
                        <UitkCardContentSection>
                          <UitkIcon name="add" />
                        </UitkCardContentSection>
                      </UitkLayoutFlexItem>
                      <UitkLayoutFlexItem grow={1}>
                        <UitkCardContentSection>
                          <UitkText>{l10n.formatText('multiProperty.playback.mixAndMatch.selectNextRoom')}</UitkText>
                        </UitkCardContentSection>
                      </UitkLayoutFlexItem>
                    </UitkLayoutFlex>
                    <UitkCardLink>
                      <Link
                        to={this.addAnotherUrl()}
                        onClick={() => {
                          this.trackAddAnotherClick('MultiProp.AddProperty');
                        }}
                      />
                    </UitkCardLink>
                  </UitkCard>
                )}
                {multiItem.showMixMatchExperience && this.state.isNotFullCart && (
                  <UitkCard>
                    <UitkLayoutFlex>
                      <UitkLayoutFlexItem alignSelf="center" grow={0}>
                        <UitkCardContentSection>
                          <UitkIcon name="add" />
                        </UitkCardContentSection>
                      </UitkLayoutFlexItem>
                      <UitkLayoutFlexItem grow={1}>
                        <UitkCardContentSection>
                          <UitkText>{l10n.formatText('multiProperty.playback.mixAndMatch.selectNextRoom')}</UitkText>
                        </UitkCardContentSection>
                      </UitkLayoutFlexItem>
                    </UitkLayoutFlex>
                    <UitkCardLink>
                      <button
                        onClick={() => {
                          this.addMixAndMatchUrl();
                          this.trackAddAnotherClick('MixMatch.AddProperty');
                        }}
                      />
                    </UitkCardLink>
                  </UitkCard>
                )}
              </li>
            </ul>
          </div>
        </UitkDialogContent>
        <HoldingAreaActions goToCheckout={goToCheckout} l10n={l10n} isAllSoldOut={isAllSoldOut} isNotFullCart={this.state.isNotFullCart} />
      </UitkFullscreenDialog>
    );
  }
}
